import React, { useContext, useEffect, useState } from 'react';
import { AuthContext } from '@/App';
import { Navigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Shield, Users, Activity, Database, TrendingUp, Eye, Flame, DollarSign, Sparkles, Users2, Heart, PlaySquare, Coins } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { getAppAnalyticsSnapshot } from '@/services/appAnalyticsSnapshot';

function formatValue(value) {
  if (value === null || value === undefined) return '--';
  return value;
}

function formatCurrency(value) {
  if (value === null || value === undefined) return '$--';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(value);
}

export default function AnalyzeApp() {
  const { user } = useContext(AuthContext);
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeframe, setTimeframe] = useState('30d');
  const [profileScope, setProfileScope] = useState('consistency');

  // Admin-only page check must come after all hooks
  const isAdmin = user?.is_internal_admin;

  const timeframes = [
    { value: '7d', label: '7 Days' },
    { value: '30d', label: '30 Days' },
    { value: '90d', label: '90 Days' },
    { value: 'all', label: 'All Time' },
  ];

  useEffect(() => {
    if (!isAdmin) return;
    
    let isMounted = true;
    getAppAnalyticsSnapshot()
      .then((data) => {
        if (isMounted) setSnapshot(data);
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [isAdmin]);

  // Admin-only page - redirect after hooks
  if (!isAdmin) {
    return <Navigate to="/app/analyze" replace />;
  }

  if (loading) {
    return (
      <div className="container mx-auto p-6 max-w-7xl">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600 mx-auto" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-7xl" data-testid="analyze-app-page">
      <div className="mb-6">
        <div className="flex flex-wrap items-center gap-3 mb-2">
          <Shield className="h-6 w-6 text-pink-500" />
          <h1 className="text-3xl font-bold text-white">App Analytics</h1>
          <Badge variant="outline" className="border-pink-500/40 text-pink-300 bg-pink-500/10 uppercase tracking-wide text-[10px]" data-testid="admin-only-badge">
            Internal Admin
          </Badge>
        </div>
        <p className="text-[#8A93A6]">Aggregated system-wide metrics. No external creator drill-downs.</p>
      </div>

      {/* Admin Notice */}
      <Alert className="bg-pink-500/10 border-pink-500/30 mb-6">
        <Shield className="h-4 w-4 text-pink-500" />
        <AlertDescription className="text-pink-400">
          <strong>Admin View:</strong> This page shows aggregated application-wide metrics across all users, agencies, and monetization signals. 
          Data is anonymized with no external creator drill-downs. Internal creators only visible in Analytics section.
        </AlertDescription>
      </Alert>

      {/* System Overview + Timeframe Selector */}
      <section className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-white">System Overview</h2>
          <div className="flex items-center gap-1 bg-white/5 rounded-lg p-0.5" data-testid="app-analytics-timeframe-selector">
            {timeframes.map((tf) => (
              <button
                key={tf.value}
                onClick={() => setTimeframe(tf.value)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  timeframe === tf.value
                    ? 'bg-pink-500/20 text-pink-300'
                    : 'text-[#8A93A6] hover:text-white'
                }`}
                data-testid={`timeframe-${tf.value}`}
              >
                {tf.label}
              </button>
            ))}
          </div>
        </div>

        {/* System Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card className="glass">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-[#8A93A6] flex items-center gap-2">
                <Users className="h-4 w-4" />
                Total Users
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white">{formatValue(snapshot?.users_total)}</div>
              <p className="text-xs text-[#8A93A6] mt-1">Active accounts</p>
            </CardContent>
          </Card>

          <Card className="glass">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-[#8A93A6] flex items-center gap-2">
                <Activity className="h-4 w-4" />
                System Health
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-500">{snapshot?.system_reliability || '--'}</div>
              <p className="text-xs text-[#8A93A6] mt-1">All systems operational</p>
            </CardContent>
          </Card>

          <Card className="glass">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-[#8A93A6] flex items-center gap-2">
                <Database className="h-4 w-4" />
                Storage Used
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white">{formatValue(snapshot?.storage_used)}</div>
              <p className="text-xs text-[#8A93A6] mt-1">GB total</p>
            </CardContent>
          </Card>

          <Card className="glass" data-testid="app-mrr-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-[#8A93A6] flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                MRR
                <Badge variant="outline" className="ml-auto text-[10px] border-white/15 text-[#8A93A6]">
                  Projected
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-[#4A5060]">$—</div>
              <p className="text-xs text-[#8A93A6] mt-1">No revenue data yet</p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* App Profile Analytics — Brand-level intelligence (Visual Mock - Read-only) */}
      <section className="mb-6" data-testid="app-profile-analytics-section">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-pink-400" />
            <h2 className="text-xl font-semibold text-white">App Profile Analytics</h2>
            <Badge variant="outline" className="text-[10px] border-white/15 text-[#8A93A6]">Read-only</Badge>
          </div>
          <Select value={profileScope} onValueChange={setProfileScope}>
            <SelectTrigger className="w-[240px] bg-[#1C1F26] border-white/10 text-white" data-testid="app-brand-scope-select">
              <SelectValue placeholder="Select app brand" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="consistency">Consistency</SelectItem>
              <SelectItem value="easymoney">EasyMoney</SelectItem>
              <SelectItem value="newera">New Era Tech Group</SelectItem>
              <SelectItem value="future">Future App</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <p className="text-sm text-[#8A93A6] mb-4">
          Brand-level performance signals across social and content ecosystems. Preview how each app brand is performing publicly.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="glass" data-testid="app-brand-audience-growth">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-[#8A93A6] flex items-center gap-2">
                <Users2 className="h-4 w-4" />
                Audience Growth
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-[#4A5060]">—</div>
              <p className="text-xs text-[#8A93A6] mt-1">Followers & subscribers</p>
            </CardContent>
          </Card>

          <Card className="glass" data-testid="app-brand-engagement">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-[#8A93A6] flex items-center gap-2">
                <Heart className="h-4 w-4" />
                Engagement
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-[#4A5060]">—</div>
              <p className="text-xs text-[#8A93A6] mt-1">Platform engagement rate</p>
            </CardContent>
          </Card>

          <Card className="glass" data-testid="app-brand-content-performance">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-[#8A93A6] flex items-center gap-2">
                <PlaySquare className="h-4 w-4" />
                Content Performance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-[#4A5060]">—</div>
              <p className="text-xs text-[#8A93A6] mt-1">Top content reach</p>
            </CardContent>
          </Card>

          <Card className="glass" data-testid="app-brand-monetization">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-[#8A93A6] flex items-center gap-2">
                <Coins className="h-4 w-4" />
                Monetization
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-[#4A5060]">$—</div>
              <p className="text-xs text-[#8A93A6] mt-1">Brand revenue signals</p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Usage Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <Card className="glass">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Content Processing
              <Badge variant="outline" className="ml-auto text-[10px] border-white/15 text-[#8A93A6]">Read-only</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8">
              <Activity className="h-12 w-12 text-[#4A5060] mx-auto mb-4" />
              <p className="text-[#8A93A6]">{snapshot?.content_throughput || 'No processing data yet'}</p>
              <p className="text-sm text-[#8A93A6] mt-2">Real-time processing metrics will appear here</p>
            </div>
          </CardContent>
        </Card>

        <Card className="glass">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              API Usage
              <Badge variant="outline" className="ml-auto text-[10px] border-white/15 text-[#8A93A6]">Read-only</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8">
              <Database className="h-12 w-12 text-[#4A5060] mx-auto mb-4" />
              <p className="text-[#8A93A6]">{snapshot?.api_usage || 'No API usage data yet'}</p>
              <p className="text-sm text-[#8A93A6] mt-2">API call statistics will appear here</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* App-Level Streak Protection Metrics (Admin-only, Read-only) */}
      <Alert className="bg-yellow-500/10 border-yellow-500/30 mb-6">
        <Eye className="h-4 w-4 text-yellow-500" />
        <AlertDescription className="text-yellow-400">
          <strong>Protection / Observation Mode:</strong> These metrics are read-only and cannot be modified. They provide insight into aggregated user behavior patterns.
        </AlertDescription>
      </Alert>
      
      <Card className="glass mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Flame className="h-5 w-5 text-orange-500" />
            App-Wide Streak Metrics
            <Badge variant="outline" className="ml-2 text-xs">Read-only</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="p-4 rounded-lg bg-white/5 border border-white/10 text-center">
              <p className="text-3xl font-bold text-white">{formatValue(snapshot?.total_active_streaks)}</p>
              <p className="text-sm text-[#8A93A6]">Active Streaks</p>
            </div>
            <div className="p-4 rounded-lg bg-white/5 border border-white/10 text-center">
              <p className="text-3xl font-bold text-white">{formatValue(snapshot?.avg_streak_length)}</p>
              <p className="text-sm text-[#8A93A6]">Avg. Streak Length</p>
            </div>
            <div className="p-4 rounded-lg bg-white/5 border border-white/10 text-center">
              <p className="text-3xl font-bold text-white">{formatValue(snapshot?.longest_streak)}</p>
              <p className="text-sm text-[#8A93A6]">Longest Streak (days)</p>
            </div>
            <div className="p-4 rounded-lg bg-white/5 border border-white/10 text-center">
              <p className="text-3xl font-bold text-white">{formatValue(snapshot?.streak_retention_rate)}</p>
              <p className="text-sm text-[#8A93A6]">Streak Retention %</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Flywheel → Valuation Wiring */}
      <Card className="glass mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-green-500" />
            Flywheel → Valuation Signals (Aggregated)
            <Badge variant="outline" className="ml-2 text-xs">Read-only</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="p-4 rounded-lg bg-white/5 border border-white/10 text-center">
              <p className="text-3xl font-bold text-white">{formatCurrency(snapshot?.total_monetization)}</p>
              <p className="text-sm text-[#8A93A6]">Total Platform Revenue</p>
            </div>
            <div className="p-4 rounded-lg bg-white/5 border border-white/10 text-center">
              <p className="text-3xl font-bold text-white">{formatValue(snapshot?.flywheel_score)}</p>
              <p className="text-sm text-[#8A93A6]">Flywheel Score</p>
            </div>
            <div className="p-4 rounded-lg bg-white/5 border border-white/10 text-center">
              <p className="text-3xl font-bold text-green-500">{formatCurrency(snapshot?.estimated_valuation)}</p>
              <p className="text-sm text-[#8A93A6]">Est. App Valuation</p>
            </div>
          </div>
          <p className="text-xs text-[#8A93A6] text-center">
            Valuation is based on aggregated MRR, user growth, and monetization signals. No per-user data is exposed.
          </p>
        </CardContent>
      </Card>

      {/* Error Logs */}
      <Card className="glass">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Recent System Events
            <Badge variant="outline" className="ml-auto text-[10px] border-white/15 text-[#8A93A6]">Read-only</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-12">
            <Shield className="h-12 w-12 text-[#4A5060] mx-auto mb-4" />
            <p className="text-[#8A93A6]">No recent events</p>
            <p className="text-sm text-[#8A93A6] mt-2">System logs and alerts will appear here</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
